import { useEffect, useRef, useState } from "react";

/**
 * Pointer-based drag reorder (works for both mouse and touch — mobile too).
 * Drag is initiated by the drag-handle only (via `handleProps(id)`), not the row.
 *
 * Usage:
 *   const { rowProps, handleProps, dragId, overId } = useReorder(items, onReorder);
 *   <div {...rowProps(item.id)} ...>
 *     <RowActions handleProps={handleProps(item.id)} ... />
 *   </div>
 *
 * The row just needs `data-drag-id` so we can locate it via elementFromPoint.
 */
export const useReorder = (items, onReorder) => {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const stateRef = useRef({ items: [], dragId: null, overId: null });

  // Keep latest items/state accessible from the imperative pointer handlers.
  useEffect(() => { stateRef.current.items = items; }, [items]);
  useEffect(() => { stateRef.current.dragId = dragId; stateRef.current.overId = overId; }, [dragId, overId]);

  const finish = () => {
    const { dragId: dId, overId: oId, items: latest } = stateRef.current;
    if (dId && oId && dId !== oId) {
      const ids = latest.map((x) => x.id);
      const from = ids.indexOf(dId);
      const to = ids.indexOf(oId);
      if (from > -1 && to > -1) {
        const next = [...ids];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        onReorder(next);
      }
    }
    setDragId(null);
    setOverId(null);
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerUp);
    // restore user-select
    document.body.style.userSelect = "";
    document.body.style.touchAction = "";
  };

  const onPointerMove = (e) => {
    if (!stateRef.current.dragId) return;
    // Prevent scroll while dragging on touch
    if (e.pointerType === "touch") e.preventDefault?.();
    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (!el) return;
    const row = el.closest("[data-drag-id]");
    if (!row) return;
    const rowId = row.getAttribute("data-drag-id");
    if (rowId && rowId !== stateRef.current.dragId && rowId !== stateRef.current.overId) {
      setOverId(rowId);
    }
  };

  const onPointerUp = () => finish();

  const handleProps = (id) => ({
    onPointerDown: (e) => {
      // Left mouse or touch/pen only
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      setDragId(id);
      stateRef.current.dragId = id;
      document.body.style.userSelect = "none";
      document.body.style.touchAction = "none";
      document.addEventListener("pointermove", onPointerMove, { passive: false });
      document.addEventListener("pointerup", onPointerUp);
      document.addEventListener("pointercancel", onPointerUp);
    },
    style: { touchAction: "none" },
  });

  const rowProps = (id) => ({
    "data-drag-id": id,
  });

  return { rowProps, handleProps, dragId, overId };
};
